import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Download, Upload, FileJson, FileText, Loader2, CheckCircle, AlertCircle, FolderOpen, BookOpen, MessageSquare, FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

type ImportResult = {
  success: boolean;
  spacesImported: number;
  sourcesImported: number;
  threadsImported: number;
  documentsImported: number;
  errors: string[];
};

export default function ExportImport() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [exportFormat, setExportFormat] = useState<"json" | "markdown">("json");
  const [exportOptions, setExportOptions] = useState({
    includeSpaces: true,
    includeSources: true,
    includeThreads: true,
    includeDocuments: true,
  });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/export", {
        format: exportFormat,
        ...exportOptions,
      });
      
      if (exportFormat === "json") {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `deeply-smart-export-${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const text = await res.text();
        const blob = new Blob([text], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `deeply-smart-export-${new Date().toISOString().split("T")[0]}.md`;
        a.click();
        URL.revokeObjectURL(url);
      }
      
      return true;
    },
    onSuccess: () => {
      toast({
        title: "Export Complete",
        description: `Your knowledge base has been exported as ${exportFormat.toUpperCase()}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Export Failed",
        description: error.message || "Could not export your data. Please try again.",
        variant: "destructive",
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/import", data);
      return res.json() as Promise<ImportResult>;
    },
    onSuccess: (data) => {
      setImportResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
      toast({
        title: "Import Complete",
        description: `Imported ${data.spacesImported + data.sourcesImported + data.documentsImported} items.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Import Failed",
        description: error.message || "Could not import your data. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".json")) {
      toast({
        title: "Invalid File",
        description: "Please select a JSON file exported from DEEPLY SMART.",
        variant: "destructive",
      });
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      importMutation.mutate(data);
    } catch (error) {
      toast({
        title: "Parse Error",
        description: "Could not parse the JSON file. Please check the file format.",
        variant: "destructive",
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const selectedCount = Object.values(exportOptions).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2" data-testid="text-page-title">Export & Import</h1>
        <p className="text-muted-foreground">Backup or transfer your knowledge base</p>
      </div>

      <Tabs defaultValue="export" className="space-y-4">
        <TabsList>
          <TabsTrigger value="export" className="gap-2" data-testid="tab-export">
            <Download className="h-4 w-4" />
            Export
          </TabsTrigger>
          <TabsTrigger value="import" className="gap-2" data-testid="tab-import">
            <Upload className="h-4 w-4" />
            Import
          </TabsTrigger>
        </TabsList>

        <TabsContent value="export">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Export Format</CardTitle>
                <CardDescription>Choose how to export your data</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={exportFormat}
                  onValueChange={(v) => setExportFormat(v as "json" | "markdown")}
                  className="space-y-3"
                >
                  <div className="flex items-start gap-3 p-3 rounded-lg border hover-elevate cursor-pointer" onClick={() => setExportFormat("json")}>
                    <RadioGroupItem value="json" id="json" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="json" className="flex items-center gap-2 cursor-pointer">
                        <FileJson className="h-5 w-5 text-chart-1" />
                        JSON Format
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Full data export. Best for backups and importing back into DEEPLY SMART.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border hover-elevate cursor-pointer" onClick={() => setExportFormat("markdown")}>
                    <RadioGroupItem value="markdown" id="markdown" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="markdown" className="flex items-center gap-2 cursor-pointer">
                        <FileText className="h-5 w-5 text-chart-2" />
                        Markdown Format
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Human-readable export. Great for sharing or reading in other apps.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Include in Export</CardTitle>
                <CardDescription>Select what to export</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <Checkbox
                    id="spaces"
                    checked={exportOptions.includeSpaces}
                    onCheckedChange={(c) => setExportOptions(o => ({ ...o, includeSpaces: !!c }))}
                    data-testid="checkbox-spaces"
                  />
                  <Label htmlFor="spaces" className="flex items-center gap-2 cursor-pointer flex-1">
                    <FolderOpen className="h-4 w-4 text-chart-3" />
                    Mastermind Spaces
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <Checkbox
                    id="sources"
                    checked={exportOptions.includeSources}
                    onCheckedChange={(c) => setExportOptions(o => ({ ...o, includeSources: !!c }))}
                    data-testid="checkbox-sources"
                  />
                  <Label htmlFor="sources" className="flex items-center gap-2 cursor-pointer flex-1">
                    <BookOpen className="h-4 w-4 text-chart-1" />
                    Reference Sources
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <Checkbox
                    id="threads"
                    checked={exportOptions.includeThreads}
                    onCheckedChange={(c) => setExportOptions(o => ({ ...o, includeThreads: !!c }))}
                    data-testid="checkbox-threads"
                  />
                  <Label htmlFor="threads" className="flex items-center gap-2 cursor-pointer flex-1">
                    <MessageSquare className="h-4 w-4 text-chart-2" />
                    Chat Threads
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <Checkbox
                    id="documents"
                    checked={exportOptions.includeDocuments}
                    onCheckedChange={(c) => setExportOptions(o => ({ ...o, includeDocuments: !!c }))}
                    data-testid="checkbox-documents"
                  />
                  <Label htmlFor="documents" className="flex items-center gap-2 cursor-pointer flex-1">
                    <FileIcon className="h-4 w-4 text-chart-4" />
                    Documents
                  </Label>
                </div>

                <Button
                  onClick={() => exportMutation.mutate()}
                  disabled={exportMutation.isPending || selectedCount === 0}
                  className="w-full mt-4"
                  data-testid="button-export"
                >
                  {exportMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export {selectedCount} {selectedCount === 1 ? "Category" : "Categories"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="import">
          <Card>
            <CardHeader>
              <CardTitle>Import Data</CardTitle>
              <CardDescription>
                Upload a JSON export file from DEEPLY SMART to import your knowledge base
              </CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />

              {!importResult ? (
                <div
                  className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="dropzone-import"
                >
                  {importMutation.isPending ? (
                    <>
                      <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
                      <p className="text-lg font-medium">Importing...</p>
                      <p className="text-muted-foreground">Processing your data</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium mb-2">Click to Upload</p>
                      <p className="text-muted-foreground">
                        Select a JSON file exported from DEEPLY SMART
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 text-green-600">
                    <CheckCircle className="h-5 w-5 shrink-0" />
                    <p className="font-medium">Import completed successfully!</p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className="text-2xl font-bold">{importResult.spacesImported}</div>
                      <div className="text-sm text-muted-foreground">Spaces</div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className="text-2xl font-bold">{importResult.sourcesImported}</div>
                      <div className="text-sm text-muted-foreground">Sources</div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className="text-2xl font-bold">{importResult.threadsImported}</div>
                      <div className="text-sm text-muted-foreground">Threads</div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className="text-2xl font-bold">{importResult.documentsImported}</div>
                      <div className="text-sm text-muted-foreground">Documents</div>
                    </div>
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="p-4 rounded-lg bg-destructive/10 text-destructive">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        <p className="font-medium">Some items could not be imported:</p>
                      </div>
                      <ul className="list-disc pl-6 text-sm space-y-1">
                        {importResult.errors.map((error, i) => (
                          <li key={i}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={() => setImportResult(null)}
                    variant="outline"
                    className="w-full"
                  >
                    Import Another File
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
